import React from 'react';
import { useIntl } from 'react-intl';
import { Button } from '@hqo/ui-atoms';
import { Loader2 } from 'lucide-react';

import { useCurrencyType } from 'hooks/use-currency-type.hook';
import { useLocale } from 'hooks/use-locale.hook';
import { ctaMessage } from '../components/step-cta';
import { summaryAmount } from '../components/estimate-summary';
import { BookingAddOnsStep } from '../use-booking-add-ons.hook';

export interface AddOnsDialogFooterProps {
  hasSelection: boolean;
  /* Minor units. The client's own sum of catalog unit prices. */
  estimateTotal: number;
  /* The currency the total is in, or null when the selection spans more than one — or lacks one. */
  estimateCurrency: string | null;
  /* A line carries no price yet, so the figure above covers only part of the order. */
  hasUnpriceable?: boolean;
  /* Whether this press places the order; see the page's own CTA. */
  commits?: boolean;
  isSubmitting: boolean;
  hasSubmitFailed: boolean;
  onContinue: VoidFunction;
  step: BookingAddOnsStep;
  isDisabled?: boolean;
  /* Absent hides the control: this footer also renders over the form step and over a first-time order. */
  canRemoveAll?: boolean;
  onRemoveAll?: VoidFunction;
  isRemoving?: boolean;
  /** The refusal, already chosen by the caller — see the cancel types. */
  removeFailureMessageId?: string | null;
}

/**
 * RES-1367 — the money and the single action, pinned under the dialog's scrolling body.
 *
 * The copy rules are imported, not rewritten. `ctaMessage` and `summaryAmount` are the shipped
 * decisions about what this step may claim, and they belong to the flow rather than to a surface.
 * Only the markup below is this dialog's own. Restating either here would give the two surfaces two
 * answers to one question, and the one that drifts would be the one nobody is looking at.
 *
 * The figure is the client's own sum of catalog unit prices, not the charge: this sum never includes
 * tax for any party, and billing's sheet states the real total at the next step. Hence Subtotal rather
 * than Total, and "Estimated total" only where no figure can be stated at all.
 */
export const AddOnsDialogFooter = ({
  hasSelection,
  estimateTotal,
  estimateCurrency,
  hasUnpriceable = false,
  commits = false,
  isSubmitting,
  hasSubmitFailed,
  onContinue,
  step,
  isDisabled = false,
  canRemoveAll = false,
  onRemoveAll,
  isRemoving = false,
  removeFailureMessageId = null,
}: AddOnsDialogFooterProps): JSX.Element => {
  const intl = useIntl();
  const locale = useLocale();
  const buildingCurrency = useCurrencyType();

  const amount = summaryAmount({ hasSelection, estimateTotal, estimateCurrency, buildingCurrency, locale });
  /* See the page's own summary: the hedge belongs to a figure that cannot be stated, which an empty
   * basket's zero is not. */
  const statesSubtotal = !hasSelection || Boolean(estimateCurrency && !hasUnpriceable);
  const label = intl.formatMessage(
    ...ctaMessage({ hasSelection, estimateTotal, estimateCurrency, locale, step, hasUnpriceable, commits }),
  );

  return (
    <div className="flex w-full flex-col gap-3" data-testid="add-ons-dialog-totals">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm text-muted-foreground">
          {intl.formatMessage({ id: statesSubtotal ? 'subtotal' : 'add_on_total_estimated' })}
        </span>
        <span className="text-base font-semibold text-foreground" data-testid="add-ons-dialog-total">
          {amount}
        </span>
      </div>

      {/*
        Why the figure above leaves something out. Gated on `hasUnpriceable` alone, never on the
        currency: a quote-only selection has no priced line and so no currency, which is the case
        this sentence exists for.
      */}
      {hasUnpriceable && (
        <p className="m-0 text-xs text-muted-foreground" data-testid="add-ons-dialog-quote-excluded">
          {intl.formatMessage({ id: 'add_on_quote_excluded' })}
        </p>
      )}

      {/* The page surface says the same thing above its own CTA; see `CtaNote` in the step's styles. */}
      {hasUnpriceable && commits && (
        <p className="m-0 text-xs text-muted-foreground" data-testid="add-ons-dialog-quote-note">
          {intl.formatMessage({ id: 'add_on_quote_no_charge' })}
        </p>
      )}

      {/*
        The server rejects a selection spanning more than one party with a 409, and refuses a
        submission for a building the feature is not on for. Neither is unreachable in normal use —
        the client-side grey-out is a convenience and explicitly not the enforcement — so the tenant
        is told rather than left looking at a button that did nothing.
      */}
      {hasSubmitFailed && (
        <p role="alert" className="m-0 text-sm text-destructive" data-testid="add-ons-dialog-error">
          {intl.formatMessage({ id: 'add_on_submit_failed' })}
        </p>
      )}

      <Button
        type="button"
        variant="default"
        className="w-full"
        onClick={onContinue}
        /*
         * ⛔ The gate is ALSO enforced in the hook's own `onSubmit`, and that redundancy is
         * deliberate: this flow has already been bitten by a shared control whose `disabled` prop
         * only greyed an icon while the click handler stayed bound.
         */
        disabled={isSubmitting || isDisabled}
        aria-busy={isSubmitting}
        data-testid="add-ons-dialog-cta"
      >
        {isSubmitting && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
        {label}
      </Button>

      {/*
        `secondary` under the primary: two filled buttons stacked give no reading order. Not
        `destructive` — this is reversible by re-ordering, and that tone is the booking's own Cancel.
      */}
      {canRemoveAll && (
        <Button
          type="button"
          variant="secondary"
          className="w-full"
          onClick={onRemoveAll}
          /* A submit in flight is about to replace this order; cancelling under it races two writers. */
          disabled={isRemoving || isSubmitting}
          aria-busy={isRemoving}
          data-testid="add-ons-dialog-remove-all"
        >
          {isRemoving && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
          {intl.formatMessage({ id: 'add_on_remove_all' })}
        </Button>
      )}

      {removeFailureMessageId && (
        <p role="alert" className="text-sm text-destructive" data-testid="add-ons-dialog-remove-all-error">
          {intl.formatMessage({ id: removeFailureMessageId })}
        </p>
      )}
    </div>
  );
};
